import {
    CompatibilityRule,
    CompatibilityRuleTypes,
    Hardware,
    isHardwareBasedFirmwareProductType,
    Product,
    ReleaseNote,
    SoftwareComponent,
    SoftwareRelease,
} from 'src/app/conf/firmware-release';
import { SoftwareReleaseTargetData } from 'src/app/model/api/data-rest';

/**
 * Dataset statico per sviluppo locale: usato da SoftwareUpdatesService
 * quando environment.production === false e FAKE_DATA === true.
 */
export const MOCK_PRODUCTS: Product[] = [
    { id: 1, name: 'XTS 0024 BUS', type: 'device', deviceModelId: 24, enabled: true, rseMandatory: 'N' },
    { id: 2, name: 'Connect App', type: 'app', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 3, name: 'Bus Calculation Tool', type: 'tool', deviceModelId: 0, enabled: false, rseMandatory: 'N' },
    { id: 4, name: 'ZNTS 0010', type: 'device', deviceModelId: 10, enabled: true, rseMandatory: 'Y' },
    { id: 5, name: 'AMOT 0015', type: 'device', deviceModelId: 15, enabled: true, rseMandatory: 'N' },
    { id: 6, name: 'VCT 0022', type: 'device', deviceModelId: 22, enabled: true, rseMandatory: 'Y' },
    { id: 7, name: 'Thermo Connect', type: 'app', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 8, name: 'Green Power Calculator', type: 'tool', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 9, name: 'RSLV 0010', type: 'device', deviceModelId: 10, enabled: false, rseMandatory: 'N' },
    { id: 10, name: 'SLAVE 0022', type: 'device', deviceModelId: 22, enabled: true, rseMandatory: 'N' },
    { id: 11, name: 'KEYPAD 0031', type: 'device', deviceModelId: 31, enabled: true, rseMandatory: 'N' },
    { id: 12, name: 'Intercom Manager', type: 'app', deviceModelId: 0, enabled: false, rseMandatory: 'N' },
    { id: 13, name: 'Firmware Upload Utility', type: 'tool', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 14, name: 'MULTI IO 0040', type: 'device', deviceModelId: 40, enabled: true, rseMandatory: 'N' },
    { id: 15, name: 'ALARM 0055', type: 'device', deviceModelId: 55, enabled: true, rseMandatory: 'Y' },
    { id: 16, name: 'Service Portal App', type: 'app', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 17, name: 'Release Notes Editor', type: 'tool', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 18, name: 'HOME AUTO 0060', type: 'device', deviceModelId: 60, enabled: false, rseMandatory: 'N' },
    { id: 19, name: 'GATE OPENER 0070', type: 'device', deviceModelId: 70, enabled: true, rseMandatory: 'N' },
    { id: 20, name: 'Technician Companion', type: 'app', deviceModelId: 0, enabled: true, rseMandatory: 'N' },
    { id: 21, name: 'Compatibility Checker', type: 'tool', deviceModelId: 0, enabled: false, rseMandatory: 'N' },
    { id: 23, name: 'CTRL 0090', type: 'board', boardName: 'ZLX24S', enabled: true, rseMandatory: 'N' },
];

/** Release di riferimento per testare la pagina di dettaglio in locale. */
export const MOCK_SHOWCASE_RELEASE_ID = 2010;

const COMPONENT_POOL = [
    'Bootloader',
    'Application',
    'Radio module',
    'Core stack',
    'Diagnostics',
    'Network stack',
    'Security module',
    'UI firmware',
    'BLE stack',
    'Motor control',
    'IO handler',
    'Config store',
    'Display driver',
    'CAN driver',
    'Update agent',
    'Filesystem',
    'Protocol handler',
    'Watchdog service',
];

const PRODUCT_1_HARDWARE: Hardware[] = [
    { id: 101, productId: 1, version: '0.0.1' },
    { id: 102, productId: 1, version: '1.2.0' },
    { id: 113, productId: 1, version: '1.3.0' },
    { id: 114, productId: 1, version: '2.0.0' },
];

export const MOCK_COMPATIBILITY_RULE_TYPES: CompatibilityRuleTypes = {
    types: [
        { name: 'KEYCODE', description: 'Utilizzato per identificare i keycode che sono abilitati al download di una release' },
        { name: 'SERIAL', description: 'Utilizzato per identificare i seriali che sono abilitati al download di una release' },
        { name: 'USER_ID', description: 'Utilizzato per identificare gli utenti che sono abilitati al download di una release' },
        { name: 'USER_ROLE', description: 'Utilizzato per identificare i ruoli che sono abilitati al download di una release' },
    ],
    operators: [
        { name: 'MIN', description: 'Elementi maggiori di MIN' },
        { name: 'MAX', description: 'Elementi minori di MAX' },
        { name: 'RANGE', description: 'Elementi compresi tra MIN e MAX' },
        { name: 'IN', description: 'Elementi compresi nella lista di valori definita' },
        { name: 'NOT_IN', description: 'Elementi non compresi nella lista di valori definita' },
    ],
};

function versionCountForProduct(product: Product): number {
    if (isHardwareBasedFirmwareProductType(product.type)) {
        return 3 + (product.id! % 4);
    }
    if (product.type === 'app') {
        return 2 + (product.id! % 3);
    }
    return 1 + (product.id! % 3);
}

function buildVersionStrings(product: Product, count: number): string[] {
    const versions = new Set<string>(['0.0.1']);
    let i = 1;
    while (versions.size < count) {
        const major = 1 + Math.floor(i / 3);
        const minor = i % 3;
        const patch = (product.id! + i) % 7;
        versions.add(`${major}.${minor}.${patch}`);
        i++;
    }
    return Array.from(versions);
}

function formatByteSize(seed: number): number {
    const kilobytes = 384 + (seed * 191) % 1792;
    return kilobytes * 1024;
}

function resolveTarget(
    installationTargetId: number,
    components: SoftwareComponent[],
    hardware: Hardware[],
): SoftwareReleaseTargetData {
    const component = components.find((item) => item.id === installationTargetId);
    if (component) {
        const hw = hardware.find((item) => item.id === component.hardwareId);
        return {
            componentId: installationTargetId,
            hardwareId: component.hardwareId,
            productId: hw?.productId,
        };
    }
    const hw = hardware.find((item) => item.id === installationTargetId);
    if (hw) {
        return {
            hardwareId: installationTargetId,
            productId: hw.productId,
        };
    }
    return { productId: installationTargetId };
}

function buildMockDataset(): {
    hardware: Hardware[];
    components: SoftwareComponent[];
    releases: SoftwareRelease[];
} {
    const hardware: Hardware[] = [];
    const components: SoftwareComponent[] = [];
    const releases: SoftwareRelease[] = [];

    let hardwareId = 101;
    let componentId = 1001;
    let releaseId = 2001;

    const addReleasesForTarget = (
        target: SoftwareReleaseTargetData,
        installationTargetId: number,
        versionBase: string,
        count: number,
    ): void => {
        for (let index = 0; index < count; index++) {
            const id = releaseId++;
            releases.push({
                id,
                version: `${versionBase}.${String(index + 1).padStart(3, '0')}`,
                installationTargetId,
                target,
                isPublished: index < count - 1 || installationTargetId % 4 !== 0,
                isMandatory: index === 0 && installationTargetId % 6 === 2,
                isComposite: id % 5 === 0,
                isEncrypted: id % 2 === 0,
                isLocalUpdate: id % 7 === 0,
                isPublic: id % 3 !== 0,
                isSecurityUpdate: id % 9 === 0,
                isSigned: id % 4 !== 0,
                byteSize: formatByteSize(installationTargetId * 11 + index),
                checksum: id % 3 === 0 ? `mock-checksum-${id}` : undefined,
                s3Filename: id % 2 === 0 ? `firmware-${id}.bin` : undefined,
                releasedAt: '2025-06-01T10:00:00Z',
                subComponents: id % 5 === 0
                    ? [
                        { name: 'UI', version: '1.0.0' },
                        { name: 'MC', version: '1.0.1' },
                    ]
                    : undefined,
            });
        }
    };

    const appendHardwareData = (entry: Hardware): void => {
        hardware.push(entry);
        const hwId = entry.id!;
        const version = entry.version ?? '1.0.0';

        const componentCount = 3 + (hwId % 4);
        const componentStart = hwId % COMPONENT_POOL.length;
        for (let index = 0; index < componentCount; index++) {
            const compId = componentId++;
            components.push({
                id: compId,
                hardwareId: hwId,
                name: COMPONENT_POOL[(componentStart + index) % COMPONENT_POOL.length],
            });
            addReleasesForTarget(
                { productId: entry.productId, hardwareId: hwId, componentId: compId },
                compId,
                `${version}.c${index + 1}`,
                1 + (compId % 3),
            );
        }

        addReleasesForTarget(
            { productId: entry.productId, hardwareId: hwId },
            hwId,
            version,
            2 + (hwId % 3),
        );
    };

    for (const product of MOCK_PRODUCTS) {
        if (!isHardwareBasedFirmwareProductType(product.type)) {
            addReleasesForTarget({ productId: product.id }, product.id!, '1.0', versionCountForProduct(product));
            continue;
        }

        if (product.id === 1) {
            PRODUCT_1_HARDWARE.forEach(appendHardwareData);
            hardwareId = 115;
            continue;
        }

        const versions = buildVersionStrings(product, versionCountForProduct(product));
        for (const version of versions) {
            appendHardwareData({ id: hardwareId++, productId: product.id, version });
        }
    }

    releases.forEach((release) => {
        if (!release.target && release.installationTargetId != null) {
            release.target = resolveTarget(release.installationTargetId, components, hardware);
        }
    });

    enrichShowcaseRelease(releases, components);

    return { hardware, components, releases };
}

function enrichShowcaseRelease(releases: SoftwareRelease[], components: SoftwareComponent[]): void {
    let showcase = releases.find((release) => release.id === MOCK_SHOWCASE_RELEASE_ID);
    if (!showcase) {
        const component = components[0];
        const hardwareId = component?.hardwareId ?? 101;
        showcase = {
            id: MOCK_SHOWCASE_RELEASE_ID,
            version: '2.4.1',
            installationTargetId: component?.id ?? 1001,
            target: {
                productId: 1,
                hardwareId,
                componentId: component?.id ?? 1001,
            },
            isPublished: true,
            isMandatory: true,
            isComposite: false,
            isEncrypted: true,
            isLocalUpdate: false,
            isPublic: true,
            isSecurityUpdate: true,
            isSigned: true,
            byteSize: 1572864,
            checksum: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
            s3Filename: 'xts-application-v2.4.1.bin',
            releasedAt: '2025-05-15T14:30:00Z',
            downloadCount: 12,
            downloadLimit: 100,
        };
        releases.push(showcase);
    } else {
        Object.assign(showcase, {
            version: '2.4.1',
            isPublished: true,
            isMandatory: true,
            isEncrypted: true,
            isPublic: true,
            isSecurityUpdate: true,
            isSigned: true,
            byteSize: 1572864,
            checksum: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
            s3Filename: 'xts-application-v2.4.1.bin',
            releasedAt: '2025-05-15T14:30:00Z',
            downloadCount: showcase.downloadCount ?? 12,
            downloadLimit: showcase.downloadLimit ?? 100,
            target: showcase.target ?? {
                productId: 1,
                hardwareId: 101,
                componentId: showcase.installationTargetId,
            },
        });
    }
}

function buildMockReleaseNotes(releases: SoftwareRelease[]): ReleaseNote[] {
    const notes: ReleaseNote[] = [];
    let noteId = 5001;

    const addNotes = (releaseId: number, itNote: string, enNote: string): void => {
        notes.push({ id: noteId++, softwareReleaseId: releaseId, language: 'it-IT', note: itNote });
        notes.push({ id: noteId++, softwareReleaseId: releaseId, language: 'en-US', note: enNote });
    };

    addNotes(
        MOCK_SHOWCASE_RELEASE_ID,
        'Correzioni stabilità radio e miglioramenti al modulo di diagnostica.\nAggiornamento consigliato per tutte le installazioni outdoor.',
        'Radio stability fixes and diagnostics module improvements.\nRecommended update for all outdoor installations.',
    );

    for (const release of releases) {
        if (release.id === MOCK_SHOWCASE_RELEASE_ID) {
            continue;
        }
        if (release.id! % 8 === 0) {
            addNotes(
                release.id!,
                `Note di rilascio mock per la versione ${release.version}.`,
                `Mock release notes for version ${release.version}.`,
            );
        }
    }

    return notes;
}

function buildMockCompatibilityRules(releases: SoftwareRelease[]): CompatibilityRule[] {
    const rules: CompatibilityRule[] = [];
    let ruleId = 6001;

    rules.push(
        { id: ruleId++, softwareReleaseId: MOCK_SHOWCASE_RELEASE_ID, type: 'KEYCODE', operator: 'RANGE', value: ['1000', '9999'] },
        { id: ruleId++, softwareReleaseId: MOCK_SHOWCASE_RELEASE_ID, type: 'SERIAL', operator: 'IN', value: ['ABC123', 'DEF456'] },
        { id: ruleId++, softwareReleaseId: MOCK_SHOWCASE_RELEASE_ID, type: 'USER_ROLE', operator: 'NOT_IN', value: ['USER'] },
    );

    for (const release of releases) {
        if (release.id === MOCK_SHOWCASE_RELEASE_ID) {
            continue;
        }
        if (release.id! % 13 === 0) {
            rules.push({
                id: ruleId++,
                softwareReleaseId: release.id,
                type: 'KEYCODE',
                operator: 'MIN',
                value: ['1000'],
            });
        }
    }

    return rules;
}

const mockDataset = buildMockDataset();

export const MOCK_HARDWARE: Hardware[] = mockDataset.hardware;
export const MOCK_COMPONENTS: SoftwareComponent[] = mockDataset.components;
export const MOCK_RELEASES: SoftwareRelease[] = mockDataset.releases;
export const MOCK_RELEASE_NOTES: ReleaseNote[] = buildMockReleaseNotes(MOCK_RELEASES);
export const MOCK_COMPATIBILITY_RULES: CompatibilityRule[] = buildMockCompatibilityRules(MOCK_RELEASES);

export function filterMockReleases(params: {
    installationTargetId?: number;
    productId?: number;
    hardwareId?: number;
    componentId?: number;
    offset?: number;
    limit?: number;
    version?: string;
}): { data: SoftwareRelease[]; pagination: { page: number; pageSize: number; pages: number; total: number } } {
    let filtered = MOCK_RELEASES.filter((release) => {
        if (params.componentId != null) {
            return release.target?.componentId === params.componentId
                || release.installationTargetId === params.componentId;
        }
        if (params.hardwareId != null) {
            return release.target?.hardwareId === params.hardwareId
                || release.installationTargetId === params.hardwareId;
        }
        if (params.installationTargetId != null) {
            return release.installationTargetId === params.installationTargetId;
        }
        if (params.productId != null) {
            if (release.target?.productId === params.productId || release.installationTargetId === params.productId) {
                return true;
            }
            const hardwareIds = MOCK_HARDWARE
                .filter((hardware) => hardware.productId === params.productId)
                .map((hardware) => hardware.id);
            return hardwareIds.includes(release.installationTargetId);
        }
        return true;
    });

    const versionSearch = params.version?.trim().toLowerCase();
    if (versionSearch) {
        filtered = filtered.filter((release) =>
            (release.version ?? '').toLowerCase().includes(versionSearch),
        );
    }

    filtered = filtered.slice().sort((a, b) =>
        (b.version ?? '').localeCompare(a.version ?? '', undefined, { numeric: true }),
    );

    const total = filtered.length;
    const offset = params.offset ?? 0;
    const limit = params.limit ?? total;
    const pageSize = limit > 0 ? limit : total;
    const data = pageSize > 0 ? filtered.slice(offset, offset + pageSize) : filtered;

    return {
        data,
        pagination: {
            page: pageSize > 0 ? Math.floor(offset / pageSize) + 1 : 1,
            pageSize,
            pages: pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1,
            total,
        },
    };
}
